import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { nanoid } from '@reduxjs/toolkit';

import type {
  CatalogImportSelection,
  CreateUserStandardInput,
  CspCatalogNode,
  CspCatalogTreeNode,
  CspJurisdiction,
  CspStandardSet,
  DeleteUserStandardInput,
  ImportCatalogStandardsInput,
  ImportCatalogStandardsResult,
  MoveUserStandardInput,
  UpdateUserStandardInput,
  UserStandard,
  UUID,
} from '@/shared/types/userStandard';
import { sbq, throwOnError } from '@/shared/utils/supabaseQuery';

const DEFAULT_SEARCH_LIMIT = 50;

type UserStandardLike = {
  id: UUID;
  parent_id: UUID | null;
  position: number;
};

type ImportCatalogStandardsRpcResult = {
  createdTopLevel?: unknown[];
  createdCount?: number;
  skippedDuplicateCount?: number;
  skippedEmptyFolderCount?: number;
};

const tagId = (id: UUID | null | undefined) => id ?? 'root';

const mapUserStandard = (r: any): UserStandard => ({
  id: r.id,
  label: r.label,
  owner_id: r.owner_id,
  parent_id: r.parent_id ?? null,
  path: r.path,
  depth: r.depth,
  position: r.position,
  title: r.title,
  node_type: r.node_type,
  code: r.code ?? null,
  description: r.description ?? null,
  statement_label: r.statement_label ?? null,
  additional_content: r.additional_content ?? {},
  csp_standard_id: r.csp_standard_id ?? null,
  created_at: r.created_at,
  updated_at: r.updated_at,
  is_deleted: r.is_deleted ?? false,
});

function compareCatalogNodes(a: CspCatalogTreeNode, b: CspCatalogTreeNode) {
  return (
    a.position - b.position ||
    a.display_code.localeCompare(b.display_code) ||
    a.csp_id.localeCompare(b.csp_id)
  );
}

function sortCatalogTree(nodes: CspCatalogTreeNode[]) {
  nodes.sort(compareCatalogNodes);
  for (const node of nodes) {
    sortCatalogTree(node.children);
    node.hasChildren = node.children.length > 0;
  }
}

export function buildCatalogTree(rows: CspCatalogNode[]): CspCatalogTreeNode[] {
  const byCspId = new Map<string, CspCatalogTreeNode>();

  for (const row of rows) {
    byCspId.set(row.csp_id, { ...row, children: [], hasChildren: false });
  }

  const roots: CspCatalogTreeNode[] = [];
  for (const node of byCspId.values()) {
    if (node.parent_csp_id && byCspId.has(node.parent_csp_id)) {
      byCspId.get(node.parent_csp_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  sortCatalogTree(roots);
  return roots;
}

export function sanitizeCatalogSearchQuery(query: string) {
  return query
    .trim()
    .replace(/[%,()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface CatalogSearchFilters {
  jurisdictionId?: string | null;
  subject?: string | null;
  educationLevel?: string | null;
  standardSetId?: string | null;
  includeDeprecated?: boolean | null;
  limit?: number | null;
}

/**
 * Sanitize the raw query and assemble the `search_csp_catalog` RPC params. The RPC does the FTS +
 * trigram hybrid and ranking server-side, so the client only sanitizes and forwards filters. A blank
 * `sanitizedQuery` signals the caller to skip the round trip entirely.
 */
export function buildCatalogSearchParams(query: string, filters: CatalogSearchFilters = {}) {
  const sanitizedQuery = sanitizeCatalogSearchQuery(query);
  return {
    sanitizedQuery,
    params: {
      p_query: sanitizedQuery,
      p_jurisdiction_id: filters.jurisdictionId ?? null,
      p_subject: filters.subject ?? null,
      p_education_level: filters.educationLevel ?? null,
      p_standard_set_id: filters.standardSetId ?? null,
      p_include_deprecated: filters.includeDeprecated ?? false,
      p_limit: Math.max(1, Math.min(filters.limit ?? DEFAULT_SEARCH_LIMIT, 200)),
    },
  };
}

/**
 * Split cart selections into the import RPC's three id sets. Whole-branch roots are expanded
 * server-side (and carry their pruned subtree roots in `excludeIds`); everything else is created
 * literally. Keeps payloads small even for large branches.
 */
export function buildCatalogImportParams(selections: CatalogImportSelection[]) {
  const literalIds = new Set<string>();
  const expandRootIds = new Set<string>();
  const excludeIds = new Set<string>();
  for (const selection of selections) {
    if (selection.withDescendants) {
      expandRootIds.add(selection.id);
      for (const excluded of selection.excludedIds) excludeIds.add(excluded);
    } else {
      literalIds.add(selection.id);
    }
  }
  return {
    literalIds: [...literalIds],
    expandRootIds: [...expandRootIds],
    excludeIds: [...excludeIds],
  };
}

// Anchor convention matches the server (us_calc_insert_position): `beforeId` is the sibling that
// should PRECEDE the item (so insert after it); `afterId` is the sibling that should FOLLOW it (so
// insert before it). When an anchor isn't in this list we fall back to the matching end.
function insertByPosition<T extends UserStandardLike>(
  arr: T[],
  item: T,
  opts: { beforeId?: UUID | null; afterId?: UUID | null; newPosition?: number | null }
) {
  const copy = arr.slice();
  if (opts.beforeId) {
    const idx = copy.findIndex((x) => x.id === opts.beforeId);
    copy.splice(idx >= 0 ? idx + 1 : copy.length, 0, item);
  } else if (opts.afterId) {
    const idx = copy.findIndex((x) => x.id === opts.afterId);
    copy.splice(idx >= 0 ? idx : 0, 0, item);
  } else if (typeof opts.newPosition === 'number' && opts.newPosition >= 0) {
    copy.splice(Math.min(opts.newPosition, copy.length), 0, item);
  } else {
    copy.push(item);
  }
  return copy;
}

async function createUserStandardRow(
  sb: any,
  input: CreateUserStandardInput
): Promise<UserStandard> {
  const id = throwOnError(
    await sb.rpc('us_create_node', {
      p_owner: input.ownerId,
      p_title: input.title,
      p_parent: input.parentId ?? null,
      p_code: input.code ?? null,
      p_description: input.description ?? null,
      p_statement_label: input.statementLabel ?? null,
      p_node_type: input.nodeType ?? 'standard',
      p_position: input.position ?? null,
      p_before: input.beforeId ?? null,
      p_after: input.afterId ?? null,
      p_csp_standard_id: input.cspStandardId ?? null,
      p_additional: input.additional ?? null,
    })
  ) as UUID;

  return mapUserStandard(
    throwOnError(await sb.from('user_standards').select('*').eq('id', id).single())
  );
}

export const standardsCatalogApi = createApi({
  reducerPath: 'standardsCatalogApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'CspJurisdiction',
    'CspStandardSet',
    'CspCatalogTree',
    'CspSearch',
    'UserStandard',
    'UserChildren',
  ],
  endpoints: (builder) => ({
    getCatalogJurisdictions: builder.query<CspJurisdiction[], void>({
      queryFn: sbq(async (sb) => {
        return throwOnError(
          await sb
            .from('csp_jurisdictions')
            .select('id, title, jurisdiction_type, url, is_active')
            .eq('is_active', true)
            .order('title')
        ) as CspJurisdiction[];
      }, 'CspJurisdictions'),
      providesTags: ['CspJurisdiction'],
    }),

    getCatalogSubjects: builder.query<
      string[],
      { jurisdictionId: string; includeDeprecated?: boolean }
    >({
      queryFn: sbq(async (sb, { jurisdictionId, includeDeprecated }) => {
        let query = sb
          .from('csp_set_facets')
          .select('subject')
          .eq('jurisdiction_id', jurisdictionId);
        if (!includeDeprecated) query = query.eq('is_deprecated', false);
        const rows = throwOnError(await query) as { subject: string }[];
        return [...new Set(rows.map((row) => row.subject))].sort();
      }, 'CspSubjects'),
      providesTags: (_result, _error, { jurisdictionId }) => [
        { type: 'CspStandardSet', id: jurisdictionId },
      ],
    }),

    getCatalogEducationLevels: builder.query<
      string[],
      { jurisdictionId: string; subject: string; includeDeprecated?: boolean }
    >({
      queryFn: sbq(async (sb, { jurisdictionId, subject, includeDeprecated }) => {
        let query = sb
          .from('csp_set_facets')
          .select('education_level')
          .eq('jurisdiction_id', jurisdictionId)
          .eq('subject', subject);
        if (!includeDeprecated) query = query.eq('is_deprecated', false);
        const rows = throwOnError(await query) as { education_level: string }[];
        return [...new Set(rows.map((row) => row.education_level))].sort();
      }, 'CspEducationLevels'),
      providesTags: (_result, _error, { jurisdictionId, subject }) => [
        { type: 'CspStandardSet', id: `${jurisdictionId}:${subject}` },
      ],
    }),

    getCatalogStandardSets: builder.query<
      CspStandardSet[],
      {
        jurisdictionId: string;
        subject: string;
        educationLevel?: string | null;
        includeDeprecated?: boolean;
      }
    >({
      queryFn: sbq(async (sb, { jurisdictionId, subject, educationLevel, includeDeprecated }) => {
        let query = sb
          .from('csp_standard_sets')
          .select('*')
          .eq('jurisdiction_id', jurisdictionId)
          .eq('subject', subject)
          .eq('is_active', true)
          .eq('csp_status_value', 'visible')
          .order('title');

        // Deprecated documents are excluded by default; the null-status case is treated as
        // not-deprecated, so it must be kept explicitly (a bare .neq would drop nulls).
        if (!includeDeprecated) {
          query = query.or(
            'document_publication_status.is.null,document_publication_status.neq.Deprecated'
          );
        }

        if (educationLevel) {
          query = query.contains('education_levels', [educationLevel]);
        }

        return throwOnError(await query) as CspStandardSet[];
      }, 'CspStandardSets'),
      providesTags: (_result, _error, { jurisdictionId, subject }) => [
        { type: 'CspStandardSet', id: `${jurisdictionId}:${subject}` },
      ],
    }),

    getCatalogSetTree: builder.query<CspCatalogTreeNode[], { standardSetId: string }>({
      queryFn: sbq(async (sb, { standardSetId }) => {
        const rows = throwOnError(
          await sb
            .from('csp_standard_catalog')
            .select('*')
            .eq('standard_set_id', standardSetId)
            .order('position', { ascending: true })
        ) as CspCatalogNode[];

        return buildCatalogTree(rows);
      }, 'CspCatalogTree'),
      providesTags: (_result, _error, { standardSetId }) => [
        { type: 'CspCatalogTree', id: standardSetId },
      ],
    }),

    searchCatalogStandards: builder.query<
      CspCatalogNode[],
      {
        query: string;
        jurisdictionId?: string | null;
        subject?: string | null;
        educationLevel?: string | null;
        standardSetId?: string | null;
        includeDeprecated?: boolean | null;
        limit?: number | null;
      }
    >({
      queryFn: sbq(async (sb, { query, ...filters }) => {
        const { sanitizedQuery, params } = buildCatalogSearchParams(query, filters);
        if (!sanitizedQuery) return [];
        return throwOnError(await sb.rpc('search_csp_catalog', params)) as CspCatalogNode[];
      }, 'CspSearch'),
      providesTags: ['CspSearch'],
    }),

    getUserStandardChildren: builder.query<
      UserStandard[],
      { ownerId: UUID; parentId?: UUID | null }
    >({
      queryFn: sbq(async (sb, { ownerId, parentId = null }) => {
        let query = sb
          .from('user_standards')
          .select('*')
          .eq('owner_id', ownerId)
          .eq('is_deleted', false)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true });

        if (parentId === null) query = query.is('parent_id', null);
        else query = query.eq('parent_id', parentId);

        return (throwOnError(await query) as any[]).map(mapUserStandard);
      }, 'UserStandards'),
      providesTags: (result, _error, { parentId }) => [
        { type: 'UserChildren', id: tagId(parentId) },
        ...(result?.map((row) => ({ type: 'UserStandard' as const, id: row.id })) ?? []),
      ],
    }),

    getAllUserStandards: builder.query<UserStandard[], { ownerId: UUID }>({
      queryFn: sbq(async (sb, { ownerId }) => {
        return (
          throwOnError(
            await sb
              .from('user_standards')
              .select('*')
              .eq('owner_id', ownerId)
              .eq('is_deleted', false)
              .order('position', { ascending: true })
              .order('created_at', { ascending: true })
          ) as any[]
        ).map(mapUserStandard);
      }, 'UserStandards'),
      providesTags: (result) => [
        { type: 'UserStandard', id: 'LIST' },
        ...(result?.map((row) => ({ type: 'UserStandard' as const, id: row.id })) ?? []),
      ],
    }),

    getUserStandardById: builder.query<UserStandard, UUID>({
      queryFn: sbq(async (sb, id) => {
        return mapUserStandard(
          throwOnError(await sb.from('user_standards').select('*').eq('id', id).single())
        );
      }, 'UserStandard'),
      providesTags: (_result, _error, id) => [{ type: 'UserStandard', id }],
    }),

    getUserStandardSubtree: builder.query<UserStandard[], { rootId: UUID }>({
      queryFn: sbq(async (sb, { rootId }) => {
        return (throwOnError(await sb.rpc('us_get_subtree', { p_root: rootId })) as any[]).map(
          mapUserStandard
        );
      }, 'UserStandards'),
      providesTags: (result) =>
        result?.map((row) => ({ type: 'UserStandard' as const, id: row.id })) ?? [],
    }),

    createUserStandard: builder.mutation<UserStandard, CreateUserStandardInput>({
      queryFn: sbq(async (sb, input) => {
        return createUserStandardRow(sb, input);
      }, 'UserStandard'),
      invalidatesTags: (_result, _error, input) => [
        { type: 'UserChildren', id: tagId(input.parentId) },
        { type: 'UserStandard', id: 'LIST' },
      ],
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const tempId = `temp-${nanoid()}`;
        const nowIso = new Date().toISOString();
        const optimistic: UserStandard = {
          id: tempId,
          label: 0,
          owner_id: input.ownerId,
          parent_id: input.parentId ?? null,
          path: '',
          depth: 0,
          position: input.position ?? 999999,
          title: input.title,
          node_type: input.nodeType ?? 'standard',
          code: input.code ?? null,
          description: input.description ?? null,
          statement_label: input.statementLabel ?? null,
          additional_content: input.additional ?? {},
          csp_standard_id: input.cspStandardId ?? null,
          created_at: nowIso,
          updated_at: nowIso,
          is_deleted: false,
        };

        const patches = [
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getUserStandardChildren',
              { ownerId: input.ownerId, parentId: input.parentId ?? null },
              (draft) => {
                const inserted = insertByPosition(draft, optimistic, {
                  beforeId: input.beforeId ?? null,
                  afterId: input.afterId ?? null,
                  newPosition: input.position ?? null,
                });
                draft.splice(0, draft.length, ...inserted);
              }
            )
          ),
        ];

        try {
          const { data } = await queryFulfilled;
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getUserStandardChildren',
              { ownerId: input.ownerId, parentId: input.parentId ?? null },
              (draft) => {
                const idx = draft.findIndex((row) => row.id === tempId);
                if (idx >= 0) draft[idx] = data;
              }
            )
          );
        } catch {
          patches.forEach((patch) => patch.undo?.());
        }
      },
    }),

    updateUserStandard: builder.mutation<UserStandard, UpdateUserStandardInput>({
      queryFn: sbq(async (sb, input) => {
        const updates: Record<string, unknown> = {};
        if (input.title !== undefined) updates.title = input.title;
        if (input.code !== undefined) updates.code = input.code;
        if (input.description !== undefined) updates.description = input.description;
        if (input.statementLabel !== undefined) updates.statement_label = input.statementLabel;
        if (input.cspStandardId !== undefined) updates.csp_standard_id = input.cspStandardId;
        if (input.additional !== undefined) updates.additional_content = input.additional;

        return mapUserStandard(
          throwOnError(
            await sb.from('user_standards').update(updates).eq('id', input.id).select('*').single()
          )
        );
      }, 'UserStandard'),
      invalidatesTags: (_result, _error, input) => [
        { type: 'UserStandard', id: input.id },
        { type: 'UserStandard', id: 'LIST' },
      ],
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const nowIso = new Date().toISOString();
        const applyPatch = (draft: UserStandard) => {
          if (input.title !== undefined) draft.title = input.title;
          if (input.code !== undefined) draft.code = input.code;
          if (input.description !== undefined) draft.description = input.description;
          if (input.statementLabel !== undefined) draft.statement_label = input.statementLabel;
          if (input.cspStandardId !== undefined) draft.csp_standard_id = input.cspStandardId;
          if (input.additional !== undefined) draft.additional_content = input.additional ?? {};
          draft.updated_at = nowIso;
        };

        const patches = [
          dispatch(
            standardsCatalogApi.util.updateQueryData('getUserStandardById', input.id, applyPatch)
          ),
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getUserStandardChildren',
              { ownerId: input.ownerId, parentId: input.parentId },
              (draft) => {
                const row = draft.find((item) => item.id === input.id);
                if (row) applyPatch(row);
              }
            )
          ),
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getAllUserStandards',
              { ownerId: input.ownerId },
              (draft) => {
                const row = draft.find((item) => item.id === input.id);
                if (row) applyPatch(row);
              }
            )
          ),
        ];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo?.());
        }
      },
    }),

    moveUserStandard: builder.mutation<void, MoveUserStandardInput>({
      queryFn: sbq(async (sb, input) => {
        const { error } = await sb.rpc('us_move_node', {
          p_id: input.id,
          p_new_parent: input.newParentId ?? null,
          p_new_position: input.newPosition ?? null,
          p_before: input.beforeId ?? null,
          p_after: input.afterId ?? null,
        });
        if (error) throw error;
        return undefined;
      }, 'UserStandard'),
      invalidatesTags: (_result, _error, input) => [
        { type: 'UserChildren', id: tagId(input.oldParentId) },
        { type: 'UserChildren', id: tagId(input.newParentId) },
        { type: 'UserStandard', id: 'LIST' },
      ],
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        if (!input.ownerId) return;

        const oldParentId = input.oldParentId ?? null;
        const newParentId = input.newParentId ?? null;
        let movedRow: UserStandard | undefined;

        const patches = [
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getUserStandardChildren',
              { ownerId: input.ownerId, parentId: oldParentId },
              (draft) => {
                const idx = draft.findIndex((row) => row.id === input.id);
                if (idx >= 0) {
                  movedRow = { ...draft[idx], parent_id: newParentId };
                  draft.splice(idx, 1);
                }
              }
            )
          ),
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getUserStandardChildren',
              { ownerId: input.ownerId, parentId: newParentId },
              (draft) => {
                if (!movedRow) return;
                const inserted = insertByPosition(
                  draft.filter((row) => row.id !== input.id),
                  movedRow,
                  {
                    beforeId: input.beforeId ?? null,
                    afterId: input.afterId ?? null,
                    newPosition: input.newPosition ?? null,
                  }
                );
                draft.splice(0, draft.length, ...inserted);
              }
            )
          ),
          // MyLibraryView renders the tree from getAllUserStandards, so the move must
          // patch this flat cache too — otherwise the row snaps back to its old spot
          // until the invalidation refetch lands (the "flash back" bug).
          dispatch(
            standardsCatalogApi.util.updateQueryData(
              'getAllUserStandards',
              { ownerId: input.ownerId },
              (draft) => {
                const moved = draft.find((row) => row.id === input.id);
                if (!moved) return;
                moved.parent_id = newParentId;

                const ordered = insertByPosition(
                  draft.filter(
                    (row) => (row.parent_id ?? null) === newParentId && row.id !== input.id
                  ),
                  moved,
                  {
                    beforeId: input.beforeId ?? null,
                    afterId: input.afterId ?? null,
                    newPosition: input.newPosition ?? null,
                  }
                );

                // buildUserStandardsTree sorts siblings by `position`, so re-sequence the
                // destination group's positions to reflect the drop order.
                ordered.forEach((row, index) => {
                  row.position = index;
                });
              }
            )
          ),
        ];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo?.());
        }
      },
    }),

    deleteUserStandard: builder.mutation<void, DeleteUserStandardInput>({
      queryFn: sbq(async (sb, { id }) => {
        const { error } = await sb.rpc('us_delete_node', { p_id: id });
        if (error) throw error;
        return undefined;
      }, 'UserStandard'),
      invalidatesTags: (_result, _error, input) => [
        { type: 'UserChildren', id: tagId(input.parentId) },
        { type: 'UserStandard', id: 'LIST' },
      ],
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patches = [];
        if (input.ownerId) {
          patches.push(
            dispatch(
              standardsCatalogApi.util.updateQueryData(
                'getUserStandardChildren',
                { ownerId: input.ownerId, parentId: input.parentId ?? null },
                (draft) => {
                  const idx = draft.findIndex((row) => row.id === input.id);
                  if (idx >= 0) draft.splice(idx, 1);
                }
              )
            )
          );
          patches.push(
            dispatch(
              standardsCatalogApi.util.updateQueryData(
                'getAllUserStandards',
                { ownerId: input.ownerId },
                (draft) => {
                  const idx = draft.findIndex((row) => row.id === input.id);
                  if (idx >= 0) draft.splice(idx, 1);
                }
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo?.());
        }
      },
    }),

    importCatalogStandards: builder.mutation<
      ImportCatalogStandardsResult,
      ImportCatalogStandardsInput
    >({
      queryFn: sbq(async (sb, input) => {
        const { literalIds, expandRootIds, excludeIds } = buildCatalogImportParams(
          input.selections
        );
        const result = throwOnError(
          await sb.rpc('us_import_catalog_standards', {
            p_owner: input.ownerId,
            p_target_parent: input.targetParentId,
            p_literal_ids: literalIds,
            p_expand_root_ids: expandRootIds,
            p_exclude_ids: excludeIds,
            p_mode: input.mode ?? 'preserveHierarchy',
            p_duplicate_action: input.duplicateAction ?? 'skip',
          })
        ) as ImportCatalogStandardsRpcResult;

        return {
          createdTopLevel: (result.createdTopLevel ?? []).map(mapUserStandard),
          createdCount: result.createdCount ?? 0,
          skippedDuplicateCount: result.skippedDuplicateCount ?? 0,
          skippedEmptyFolderCount: result.skippedEmptyFolderCount ?? 0,
        };
      }, 'UserStandardsImport'),
      invalidatesTags: (_result, _error, input) => [
        { type: 'UserChildren', id: tagId(input.targetParentId) },
        { type: 'UserStandard', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCatalogJurisdictionsQuery,
  useGetCatalogSubjectsQuery,
  useGetCatalogEducationLevelsQuery,
  useGetCatalogStandardSetsQuery,
  useGetCatalogSetTreeQuery,
  useLazyGetCatalogSetTreeQuery,
  useSearchCatalogStandardsQuery,
  useLazySearchCatalogStandardsQuery,
  useGetUserStandardChildrenQuery,
  useLazyGetUserStandardChildrenQuery,
  useGetAllUserStandardsQuery,
  useGetUserStandardByIdQuery,
  useGetUserStandardSubtreeQuery,
  useCreateUserStandardMutation,
  useUpdateUserStandardMutation,
  useMoveUserStandardMutation,
  useDeleteUserStandardMutation,
  useImportCatalogStandardsMutation,
} = standardsCatalogApi;
